import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, CheckCircle2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { usePromoteImportJob } from '../useImportJob';
import type { ImportJobDto, PromoteResult } from '../types';

interface PromoteActionProps {
  job: ImportJobDto | undefined;
  jobId: string | undefined;
}

export const PromoteAction = ({ job, jobId }: PromoteActionProps) => {
  const { t } = useTranslation();
  const promote = usePromoteImportJob(jobId);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState<PromoteResult | null>(null);

  const canPromote = job?.status === 'ready_to_promote';
  const isPromoted = job?.status === 'promoted';

  const handleConfirm = async () => {
    const res = await promote.mutateAsync();
    setResult(res);
  };

  return (
    <>
      <Button
        type="button"
        onClick={() => setConfirmOpen(true)}
        disabled={!canPromote || promote.isPending}
      >
        {promote.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {isPromoted
          ? t('efs.imports.statuses.promoted', { ns: 'imports' })
          : t('efs.imports.review.actions.promote', { ns: 'imports' })}
      </Button>

      <Dialog
        open={confirmOpen}
        onOpenChange={(o) => {
          setConfirmOpen(o);
          if (!o) setResult(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {result
                ? t('efs.imports.review.promote.result_title', { ns: 'imports' })
                : t('efs.imports.review.promote.title', { ns: 'imports' })}
            </DialogTitle>
            <DialogDescription>
              {result ? null : t('efs.imports.review.promote.confirm', { ns: 'imports' })}
            </DialogDescription>
          </DialogHeader>

          {result ? (
            <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-2 text-sm">
              {result.inbound_promoted !== undefined ? (
                <>
                  <dt className="text-efs-text-muted">
                    <CheckCircle2 className="mr-1 inline h-4 w-4 text-efs-success" />
                    {t('efs.imports.review.promote.inbound_lines_created', { ns: 'imports' })}
                  </dt>
                  <dd className="font-mono">{result.inbound_promoted}</dd>
                </>
              ) : null}
              {result.dismantling_promoted !== undefined ? (
                <>
                  <dt className="text-efs-text-muted">
                    <CheckCircle2 className="mr-1 inline h-4 w-4 text-efs-success" />
                    {t('efs.imports.review.promote.dismantling_outputs_created', {
                      ns: 'imports',
                    })}
                  </dt>
                  <dd className="font-mono">{result.dismantling_promoted}</dd>
                </>
              ) : null}
            </dl>
          ) : null}

          <DialogFooter>
            {result ? (
              <Button type="button" onClick={() => setConfirmOpen(false)}>
                {t('efs.common.actions.close')}
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setConfirmOpen(false)}
                  disabled={promote.isPending}
                >
                  {t('efs.common.actions.cancel')}
                </Button>
                <Button type="button" onClick={handleConfirm} disabled={promote.isPending}>
                  {promote.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {t('efs.imports.review.actions.confirm', { ns: 'imports' })}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
